import React, { useState, useEffect } from 'react';
import { DatePicker, Select, Table, Row, Col, Card, Spin, Button, message } from 'antd';
import moment from 'moment';
import { getClients, getClientBilling, exportClientBillingPdf } from '../api';
import { DownloadOutlined } from '@ant-design/icons';

const { Option } = Select;

const ClientBilling = () => {
  const [selectedMonth, setSelectedMonth] = useState(moment());
  const [selectedClient, setSelectedClient] = useState();
  const [data, setData] = useState([]);
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    setLoadingClients(true);
    getClients().then(res => {
      if (res.success) setClients(res.data);
      setLoadingClients(false);
    });
  }, []);

  useEffect(() => {
    if (selectedClient && selectedMonth) {
      setLoadingData(true);
      getClientBilling({ client_id: selectedClient, month: selectedMonth.format('YYYY-MM') })
        .then(res => {
          console.log(res)
          if (res.data.success && Array.isArray(res.data.data)) setData(res.data.data);
          else setData([]);
          setLoadingData(false);
        });
    } else {
      setData([]);
    }
  }, [selectedClient, selectedMonth]);

  const columns = [
    { 
      title: 'Employee', 
      dataIndex: 'employee', 
      key: 'employee',
      render: (text, record) => (
        <span style={{ color: record.type === 'subcontractor' ? '#52c41a' : '#1890ff', fontWeight: 500 }}>
          {text}
        </span>
      )
    },
    { title: 'Employee Code', dataIndex: 'emp_code', key: 'emp_code' },
    { title: 'Hours Worked', dataIndex: 'hours_worked', key: 'hours_worked' },
    { title: 'Hourly Rate', dataIndex: 'hourly_rate', key: 'hourly_rate', render: v => `AED ${v}` },
    { title: 'Total Amount', dataIndex: 'total_amount', key: 'total_amount', render: v => `AED ${v}` },
  ];

  const handleDownloadPdf = async () => {
    if (!selectedClient || !selectedMonth) {
      message.warning('Please select a client and month first.');
      return;
    }
    setDownloading(true);
    try {
      const blob = await exportClientBillingPdf({ client_id: selectedClient, month: selectedMonth.format('YYYY-MM') });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `client_billing_${selectedClient}_${selectedMonth.format('YYYY-MM')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      message.error('Failed to download PDF');
    } finally {
      setDownloading(false);
    }
  };

  // Calculate total hours and average hourly rate
  const totalHours = Array.isArray(data) ? data.reduce((sum, row) => sum + (Number(row.hours_worked) || 0), 0) : 0;
  const avgRate = Array.isArray(data) && data.length > 0 ? (data.reduce((sum, row) => sum + (Number(row.hourly_rate) || 0), 0) / data.length) : 0;

  return (
    <Card title="Client Billing">
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col>
          <DatePicker
            picker="month"
            value={selectedMonth}
            onChange={setSelectedMonth}
            style={{ width: 160 }}
          />
        </Col>
        <Col>
          <Select
            allowClear
            placeholder="Select Client"
            value={selectedClient}
            onChange={setSelectedClient}
            style={{ width: 200 }}
            loading={loadingClients}
            showSearch
            optionFilterProp="children"
          >
            {clients.map(client => (
              <Option key={client.id} value={client.id}>{client.name}</Option>
            ))}
          </Select>
        </Col>
        <Col>
          <Button type="primary" icon={<DownloadOutlined />} onClick={handleDownloadPdf} loading={downloading}>
            Download PDF
          </Button>
        </Col>
      </Row>
      <Row gutter={16} style={{ marginBottom: 8 }}>
        <Col>
          <Card size="small" style={{ display: 'inline-block', marginRight: 16 }}>
            <b>Total Hours:</b> {totalHours}
          </Card>
          <Card size="small" style={{ display: 'inline-block' }}>
            <b>Average Hourly Rate:</b> AED {avgRate.toFixed(2)}
          </Card>
        </Col>
      </Row>
      <Spin spinning={loadingData} tip="Loading billing data...">
        <Table columns={columns} dataSource={Array.isArray(data) ? data : []} rowKey={row => row.emp_code || row.employee} bordered />
      </Spin>
    </Card>
  );
};

export default ClientBilling; 